import '../styles/legend-panel.css';

import { store } from '../state';
import { getLayerById, getEffectiveVisibility } from '../layers';
import { getChoroplethLegend, getGradientLegend } from '../layer-styles';
import { getConfigOptional } from '../config';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';

const els = {
  legendPanel: document.getElementById('legend-panel') as HTMLElement,
  legendContent: document.getElementById('legend-content') as HTMLElement,
  legendCloseBtn: document.getElementById('legend-close-btn') as HTMLButtonElement,
};

let manuallyClosed = false;

/**
 * Проверяет, является ли источник векторным.
 */
function isVectorSource(source: any): boolean {
  return source && typeof source.getFeatures === 'function';
}

/**
 * Проверяет, какие классы реально присутствуют в данных слоя.
 * Для не-векторных слоёв (WMS) возвращает все классы.
 */
function filterClassesByData(
  layerId: string, 
  classes: Array<{ name: string; min: number; max: number; color: string }>
): Array<{ name: string; min: number; max: number; color: string }> {
  const layer = getLayerById(layerId) as VectorLayer<any>;
  if (!layer) return classes;

  const source = layer.getSource();
  if (!source || !isVectorSource(source)) return classes; // <-- НОВОЕ: проверка

  const features = source.getFeatures();
  if (features.length === 0) return [];

  const styles = getConfigOptional('layer-styles');
  const layerStyle = styles?.[layerId] as any;
  if (!layerStyle?.choropleth?.attribute) return classes;

  const attributeName = layerStyle.choropleth.attribute;

  const values = new Set<number>();
  features.forEach(feature => {
    const rawValue = feature.get(attributeName);
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    if (typeof value === 'number' && !isNaN(value)) {
      values.add(value);
    }
  });

  if (values.size === 0) return [];

  return classes.filter(cls => {
    for (const value of values) {
      if (value >= cls.min && value < cls.max) {
        return true;
      }
    }
    return false;
  });
}

/**
 * Проверяет, есть ли в данных значения вне всех классов.
 * Для не-векторных слоёв возвращает false.
 */
function hasNoDataValues(layerId: string): boolean {
  const layer = getLayerById(layerId) as VectorLayer<any>;
  if (!layer) return false;

  const source = layer.getSource();
  if (!source || !isVectorSource(source)) return false; // <-- НОВОЕ: проверка

  const features = source.getFeatures();
  if (features.length === 0) return false;

  const styles = getConfigOptional('layer-styles');
  const layerStyle = styles?.[layerId] as any;
  if (!layerStyle?.choropleth?.attribute) return false;

  const attributeName = layerStyle.choropleth.attribute;
  const legendData = getChoroplethLegend(layerId);
  const classes = legendData?.classes || [];

  for (const feature of features) {
    const rawValue = feature.get(attributeName);
    
    if (rawValue === null || rawValue === undefined) return true;
    
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    if (typeof value !== 'number' || isNaN(value)) return true;
    
    if (classes.length > 0) {
      const minClassValue = classes[0].min;
      if (value < minClassValue) return true;
    }
  }

  return false;
}

/**
 * Проверяет, есть ли значения в заданном диапазоне.
 * Для не-векторных слоёв возвращает false.
 */
function hasValuesInRange(layerId: string, min: number, max: number): boolean {
  const layer = getLayerById(layerId) as VectorLayer<any>;
  if (!layer) return false;

  const source = layer.getSource();
  if (!source || !isVectorSource(source)) return false; // <-- НОВОЕ: проверка

  const features = source.getFeatures();
  if (features.length === 0) return false;

  const styles = getConfigOptional('layer-styles');
  const layerStyle = styles?.[layerId] as any;
  const attributeName = layerStyle?.gradient?.attribute || layerStyle?.choropleth?.attribute;
  if (!attributeName) return false;

  for (const feature of features) {
    const rawValue = feature.get(attributeName);
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    if (typeof value === 'number' && !isNaN(value) && value >= min && value <= max) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// Получение списка активных слоёв с легендой
// ============================================================================

interface ActiveLegend {
  layerId: string;
  layerName: string;
  type: 'choropleth' | 'gradient';
  classes?: Array<{ name: string; min: number; max: number; color: string }>;
  gradient?: {
    stops: Array<{ value: number; color: string }>;
    min: number;
    max: number;
  };
  noData?: { color: string };
}

function getActiveLegends(): ActiveLegend[] {
  const layersConfig = getConfigOptional('layers');
  if (!layersConfig) return [];

  const result: ActiveLegend[] = [];

  const collectLayers = (groups: any[]) => {
    for (const group of groups) {
      if (group.layers) {
        for (const layer of group.layers) {
          if (layer.hidden) continue;
          if (!getEffectiveVisibility(layer.id)) continue;
          
          // Проверяем загруженность фич
          const olLayer = getLayerById(layer.id) as VectorLayer<any>;
          if (olLayer) {
            const source = olLayer.getSource() as VectorSource;
            if (source && isVectorSource(source)) {
              const features = source.getFeatures();
              if (features.length === 0) continue;
            }
          }

          // Проверяем тип легенды
          const choroplethData = getChoroplethLegend(layer.id);
          const gradientData = getGradientLegend(layer.id);
          
          if (!choroplethData && !gradientData) continue;

          let legendEntry: ActiveLegend | null = null;

          if (gradientData) {
            // Градиентная легенда
            const showNoData = gradientData.noData && hasNoDataValues(layer.id);
            
            if (showNoData) {
              legendEntry = {
                layerId: layer.id,
                layerName: layer.name,
                type: 'gradient',
                gradient: {
                  stops: gradientData.stops,
                  min: gradientData.min,
                  max: gradientData.max
                },
                noData: gradientData.noData
              };
            } else {
              // Проверяем, есть ли значения в диапазоне градиента
              if (hasValuesInRange(layer.id, gradientData.min, gradientData.max)) {
                legendEntry = {
                  layerId: layer.id,
                  layerName: layer.name,
                  type: 'gradient',
                  gradient: {
                    stops: gradientData.stops,
                    min: gradientData.min,
                    max: gradientData.max
                  }
                };
              }
            }
          } else if (choroplethData) {
            // Хороплетная легенда (существующая логика)
            const filteredClasses = filterClassesByData(layer.id, choroplethData.classes);
            const showNoData = choroplethData.noData && hasNoDataValues(layer.id);

            if (filteredClasses.length > 0 || showNoData) {
              legendEntry = {
                layerId: layer.id,
                layerName: layer.name,
                type: 'choropleth',
                classes: filteredClasses,
                noData: showNoData ? choroplethData.noData : undefined
              };
            }
          }

          if (legendEntry) {
            result.push(legendEntry);
          }
        }
      }
      if (group.groups) {
        collectLayers(group.groups);
      }
    }
  };

  collectLayers(layersConfig.groups);
  return result;
}

// ============================================================================
// Рендеринг легенды (без изменений)
// ============================================================================

function renderLegend(): void {
  const state = store.getState();
  if (state.currentView !== 'map') {
    els.legendPanel.style.display = 'none';
    return;
  }

  const activeLegends = getActiveLegends();

  if (activeLegends.length === 0) {
    els.legendPanel.style.display = 'none';
    return;
  }

  if (manuallyClosed) {
    els.legendPanel.style.display = 'none';
    return;
  }

  let html = '';

  activeLegends.forEach((legend) => {
    html += `
      <div class="legend-layer">
        <div class="legend-layer-title">${escapeHtml(legend.layerName)}</div>
    `;

    if (legend.type === 'gradient' && legend.gradient) {
      // Градиентная легенда — цветовая шкала
      html += renderGradientLegend(legend.gradient);
    } else if (legend.type === 'choropleth' && legend.classes) {
      // Хороплетная легенда — список классов
      html += `<ul class="legend-classes">`;
      legend.classes.forEach((cls) => {
        const rangeText = cls.max === Infinity 
          ? `≥ ${formatNumber(cls.min)}`
          : `${formatNumber(cls.min)} — ${formatNumber(cls.max)}`;

        html += `
          <li class="legend-class">
            <span class="legend-color-box" style="background-color: ${cls.color};"></span>
            <span class="legend-class-label">${escapeHtml(cls.name)}</span>
            <span class="legend-class-range">${rangeText}</span>
          </li>
        `;
      });
      html += `</ul>`;
    }

    // "Нет данных" для обоих типов
    if (legend.noData) {
      html += `
        <ul class="legend-classes">
          <li class="legend-class legend-class-nodata">
            <span class="legend-color-box legend-color-nodata" style="background-color: ${legend.noData.color};"></span>
            <span class="legend-class-label">Нет данных</span>
          </li>
        </ul>
      `;
    }

    html += `</div>`;
  });

  els.legendContent.innerHTML = html;
  els.legendPanel.style.display = 'flex';

  updateLegendPosition();
}

/**
 * Рендерит градиентную легенду (цветовая шкала).
 */
function renderGradientLegend(gradient: {
  stops: Array<{ value: number; color: string }>;
  min: number;
  max: number;
}): string {
  // Создаём CSS gradient
  const gradientStops = gradient.stops
    .map(stop => {
      const percent = ((stop.value - gradient.min) / (gradient.max - gradient.min)) * 100;
      return `${stop.color} ${percent}%`;
    })
    .join(', ');

  const cssGradient = `linear-gradient(to right, ${gradientStops})`;

  // Подписи значений
  const labels = gradient.stops.map(stop => {
    const percent = ((stop.value - gradient.min) / (gradient.max - gradient.min)) * 100;
    return `<span class="legend-gradient-label" style="left: ${percent}%;">${formatNumber(stop.value)}</span>`;
  }).join('');

  return `
    <div class="legend-gradient">
      <div class="legend-gradient-bar" style="background: ${cssGradient};"></div>
      <div class="legend-gradient-labels">
        ${labels}
      </div>
    </div>
  `;
}

// ============================================================================
// Позиционирование (без изменений)
// ============================================================================

function updateLegendPosition(): void {
  const state = store.getState();
  const hasSidebarContent = state.selectedObservation 
    || state.selectedTraverse 
    || state.selectedSite;
  
  if (hasSidebarContent) {
    els.legendPanel.classList.add('shifted');
  } else {
    els.legendPanel.classList.remove('shifted');
  }
}

// ============================================================================
// Инициализация (без изменений)
// ============================================================================

export function initLegendPanel(): void {
  console.log('🔧 Инициализация панели легенды');

  els.legendCloseBtn.addEventListener('click', () => {
    manuallyClosed = true;
    els.legendPanel.style.display = 'none';
  });

  store.subscribe(() => {
    renderLegend();
  });

  setupLayerVisibilityListeners();
  renderLegend();
}

function setupLayerVisibilityListeners(): void {
  const layersConfig = getConfigOptional('layers');
  if (!layersConfig) return;

  const attachListeners = (groups: any[]) => {
    for (const group of groups) {
      if (group.layers) {
        for (const layer of group.layers) {
          const olLayer = getLayerById(layer.id);
          if (olLayer) {
            // При изменении видимости слоя
            olLayer.on('change:visible', () => {
              manuallyClosed = false;
              renderLegend();
            });

            // При загрузке новых фич (для WFS с bbox-стратегией)
            const source = (olLayer as VectorLayer<any>).getSource();
            if (source && isVectorSource(source)) {
              source.on('featuresloadend', () => {
                renderLegend();
              });
              source.on('changefeature', () => {
                renderLegend();
              });
            }
          }
        }
      }
      if (group.groups) {
        attachListeners(group.groups);
      }
    }
  };

  attachListeners(layersConfig.groups);
}

// ============================================================================
// Вспомогательные функции (без изменений)
// ============================================================================

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(3).replace(/\.?0+$/, '');
}

function escapeHtml(str: string): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}